import {useContext} from 'react';
import {useTranslation} from 'react-i18next';
import {NavigationContext, NavigationRouteContext} from '@react-navigation/native';

import {parentOf, trailOf, isUnder, type Crumb} from '@/shared/config/screens';

/**
 * «Где я и куда ведёт назад» — один ответ на оба вопроса, для любого экрана.
 *
 * ## Почему это не `navigation.goBack()`
 *
 * `goBack` — это шаг по ИСТОРИИ, а человек нажимает стрелку, чтобы подняться на
 * уровень ВВЕРХ. Пока в экран ведёт одна дверь, это одно и то же. Как только дверей
 * две, история начинает водить кругами: из отчёта по отделу открыли настройки этого
 * отдела, нажали «назад» — и вернулись в отчёт, хотя стояли в настройках и ждали
 * увидеть список отделов.
 *
 * Здесь стрелка ведёт к РОДИТЕЛЮ из [`shared/config/screens`](../config/screens.ts),
 * и ровно одно исключение сделано в пользу истории: если предыдущий экран стека — сам
 * родитель или кто-то из его потомков, делается обычный шаг назад. Это спуск по
 * лестнице «компания → отдел → сотрудник»: подниматься по ней надо в тот отдел, из
 * которого спускались, а не в её начало.
 *
 * Во всех остальных случаях зовётся `popTo` — «вернуться к этому экрану в стеке, а
 * если его там нет, поставить его на место текущего». Именно поэтому «назад» из
 * настроек отдела приводит в «Отделы» и тогда, когда в «Отделах» ещё ни разу не были.
 *
 * ## Вне навигатора
 *
 * Контексты читаются напрямую, а не через `useRoute`/`useNavigation`: те бросают
 * исключение, когда навигатора над ними нет, и любой тест, отрисовавший страницу в
 * одиночку, падал бы на каркасе вместо того, что он проверяет. Нет навигатора —
 * нет ни крошек, ни стрелки, и это верно: подниматься не откуда.
 */
export type PageLink = {label: string; onPress: () => void};

export type PageNav = {
  /** Путь ДО этой страницы, от корня раздела. Пусто у корневых экранов. */
  trail: PageLink[];
  /** На уровень вверх. Отсутствует у корневых экранов. */
  back?: PageLink;
};

/** Ровно то, что нужно от навигации стека; остального у вкладки может и не быть. */
type StackNav = {
  canGoBack?: () => boolean;
  goBack?: () => void;
  popTo?: (name: string, params?: object) => void;
  navigate?: (name: string, params?: object) => void;
  getState?: () =>
    | {routes: {key: string; name: string}[]; routeNames?: string[]}
    | undefined;
};

export function usePageNav(): PageNav {
  const {t} = useTranslation();
  const route = useContext(NavigationRouteContext);
  const navigation = useContext(NavigationContext) as StackNav | undefined;

  if (!route || !navigation) {
    return {trail: []};
  }

  const params = route.params as Record<string, unknown> | undefined;
  const label = (crumb: Crumb): string =>
    crumb.title ??
    (crumb.titleKey
      ? t(crumb.titleKey)
      : crumb.fallbackKey
      ? t(crumb.fallbackKey)
      : crumb.screen);

  /**
   * Перемотать стек к этому экрану. `popTo` — «вернуться к нему, а если его в стеке
   * нет, поставить его на место текущего»; у вкладки без стека его нет, и там
   * `navigate` делает ровно то же самое.
   */
  const rewindTo = (crumb: Crumb) => {
    (navigation.popTo ?? navigation.navigate)?.(crumb.screen, crumb.params);
  };

  /**
   * Стрелка «назад»: на уровень вверх — но обычным шагом по истории, если история и
   * так стоит под этим родителем. Это спуск по лестнице отчётов: из отчёта по
   * сотруднику надо вернуться в ТОТ отдел, из которого спустились, а не в начало
   * лестницы. Во всех прочих случаях стек перематывается к родителю.
   */
  const goUp = (parent: Crumb) => () => {
    const stack = navigation.getState?.();
    const here = stack?.routes.findIndex(r => r.key === route.key) ?? -1;
    const previous = here > 0 ? stack?.routes[here - 1] : undefined;

    if (previous && isUnder(previous.name, parent.screen) && navigation.canGoBack?.()) {
      navigation.goBack?.();
      return;
    }
    rewindTo(parent);
  };

  /**
   * Подниматься можно только внутри своего стека. Один и тот же экран бывает в двух:
   * «Отделы» лежат в «Настройках», а у компании из одних камер стоят своей вкладкой
   * (`OrgStack`), где над ними ничего нет. Родитель, которого в этом стеке нет, —
   * не уровень вверх, а прыжок в чужой раздел, и стрелки туда быть не должно.
   */
  const names = navigation.getState?.()?.routeNames;
  const reachable = (crumb: Crumb) => !names || names.includes(crumb.screen);
  const fullTrail = trailOf(route.name, params);
  const cut = fullTrail.length - [...fullTrail].reverse().findIndex(c => !reachable(c));
  const trail = fullTrail.every(reachable) ? fullTrail : fullTrail.slice(cut);
  const up = parentOf(route.name, params);
  const parent = up && reachable(up) ? up : undefined;

  return {
    // Крошка перематывает ВСЕГДА, без поблажки истории: нажали «Настройки» — надо
    // оказаться в настройках, а не шагнуть на один экран назад, который случайно
    // тоже лежит под ними.
    trail: trail.map(crumb => ({
      label: label(crumb),
      onPress: () => rewindTo(crumb),
    })),
    back: parent ? {label: label(parent), onPress: goUp(parent)} : undefined,
  };
}

/**
 * «На уровень вверх» для вызова из кода — после сохранения формы.
 *
 * Ровно то же действие, что у стрелки в шапке, и берётся оно из той же карты: экран,
 * закрывшийся сам, обязан оставить человека там же, куда его увела бы стрелка.
 * `goBack()` здесь возвращал бы в историю — а форму сотрудника открывают и по прямой
 * ссылке, где никакой истории нет.
 *
 * Уходить после УДАЛЕНИЯ этим не следует: у карточки удалённого предмета родитель —
 * она сама (правка растёт из карточки), и подниматься надо явно в список. См.
 * `PersonnelCardScreen`.
 */
export function useGoUp(): () => void {
  const {back} = usePageNav();
  return back?.onPress ?? (() => {});
}
